<template>
  <div class="modal-container">
    <div class="wraper-content">
      <div class="position-btn-close">
        <div class="close-box-btn">
          <AppIcon name="close" width="16" @click="closeModal" />
        </div>
      </div>
      <div class="wraper-title">
        <h1 class="header">
          {{ $t('_web_login-account-modal', 'Login to your account') }}
        </h1>
        <h3 class="text-modal">
          {{ $t('_web_donthave-account-modal', `Don't have an account? `)
          }}<a @click="openSignUpModal">{{
            $t('_web_register-account-modal', ' Register')
          }}</a>
        </h3>
      </div>
      <form class="form-input" @submit.prevent="formSubmit">
        <InputEmail
          v-model.trim="email"
          v-focus
          v-validate="'required|email|max:128'"
          :placeholder="$t('_web_email_placeholder', 'your@email.com')"
          :name="this.$$t('Email', 'email-label')"
          type="email"
          :label="$$t('Email', 'email-label')"
          :tabindex="0"
          autocomplete-off
          autofocus
          required
          variant=""
          :error="errors.first(this.$$t('Email', 'email-label'))"
        />
        <InputPassword
          v-model.trim="password"
          v-validate="'required|min:8|max:64'"
          :placeholder="'Password'"
          :name="'Password'"
          autocomplete-off
          :tabindex="0"
          required
          variant=""
          :forgot-pass="'Forgot password?'"
          :label="'Enter your password'"
          :error="errors.first($t('_web_password_placeholder', 'Password'))"
        />

        <button class="text-truncate new-signup-btn" type="submit">
          Sign In
        </button>
      </form>
      <div class="text-danger">
        {{
          authError &&
            $t(
              '_web_server_errors_incorrect_credentials_email',
              'Incorrect Email or Password.'
            )
        }}
      </div>
      <div class="d-flex row justify-content-center w-100 align-items-center">
        <GoogleSignIn />
        <TwitterSignIn />
        <AppleSignIn />
        <MetacoSignIn
          v-if="currentRegion.id === 'indonesia' || metacoTest === 'true'"
        />
      </div>
    </div>
    <ModalBackdrop
      v-model="isLoading"
      class="sign-in-loader"
      :style="{ width: `100%` }"
      :click_on_backdrop="false"
      :esc_key="false"
      :variant="'loader'"
    >
      <img
        src="~@/assets/images/playhera-indo/Playhera_Loader.svg"
        alt="Loader"
      />
    </ModalBackdrop>
  </div>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { translate } from '@/mixins/translate';
import AppIcon from '@/components/atoms/Icon';

import isLoadingMixin from '@/mixins/isLoading.js';
import { PRIVACY_LINK, TERMS_LINK } from '@/api/config';
import InputEmail from '@/components/form/NewInputEmail';
import InputPassword from '@/components/form/NewInputPassword';
import GoogleSignIn from '@/components/auth/GoogleSignIn';
import TwitterSignIn from '@/components/auth/TwitterSignIn';
import AppleSignIn from '@/components/auth/AppleSignIn';
import ModalBackdrop from '@/components/common/ModalBackdrop.vue';
import MetacoSignIn from '@/components/auth/MetacoSignIn.vue';

const SAVING_KEY = 'saveAuth';

export default {
  name: 'SignInModal',
  components: {
    InputEmail,
    InputPassword,
    GoogleSignIn,
    TwitterSignIn,
    AppleSignIn,
    ModalBackdrop,
    AppIcon,
    MetacoSignIn,
  },
  inject: ['$validator'],
  mixins: [isLoadingMixin, translate('_web_signup')],

  data() {
    return {
      uniqId: Date.now(),
      email: null,
      login: null,
      password: null,
      country: null,
      agreements: [],
      agreementsTextProps: {
        terms: {
          text: 'Terms',
          href: TERMS_LINK,
        },

        privacy: {
          text: 'Privacy',
          href: PRIVACY_LINK,
        },
      },
      haveAccountTextProps: {
        login: {
          text: this.$$t('Login'),
          to: {
            name: 'signin',
          },
        },
      },
    };
  },

  computed: {
    ...mapState('my', ['account']),
    ...mapGetters(['currentRegion']),
    ...mapState('current', ['account', 'accountId']),
    autocomplete() {
      return this.saveAuth ? 'on' : 'new-password';
    },
    idSuff() {
      return this.saveAuth ? 'save' : this.uniqId;
    },
    authError() {
      // please not use errors.firstNot('login')
      const errorsCollect = Object.keys(this.errors.collect());
      if (errorsCollect.some(field => field !== 'login')) {
        return '';
      }

      return this.errors.first('login');
    },
  },
  watch: {
    '$route.name'() {
      this.$store.dispatch('closeModal');
    },
  },

  methods: {
    ...mapActions(['authRequest', 'openModal', 'closeModal']),
    openSignUpModal() {
      this.closeModal();
      this.openModal({
        name: 'SignUpModal',
        settings: {
          clickOnBackdrop: false,
          escKey: false,
          variant: 'transparent',
        },
      });
    },
    formSubmit() {
      this.errors.clear();
      if (!this.errors.count()) {
        this.setLoading();
        this.$validator
          .validateAll()
          .then(
            result =>
              result
                ? Promise.resolve({
                    login: this.email,
                    password: this.password,
                    scopes: 'application,administrative',
                  })
                : Promise.reject() // eslint-disable-line
          )
          .then(this.authRequest)
          .then(() => {
            this.$emit('authorized');
            this.closeModal();
          })
          .then(
            window.dataLayer.push({
              event: 'login',
              email: this.email,
              from: this.$route.path,
            })
          )
          .catch(err => {
            this.unsetLoading();
            const msg = err && err.message;

            if (!msg) {
              return;
            }

            this.errors.add({
              field: 'login',
              msg,
            });
          })
          .finally(this.unsetLoading(), this.errors.clear());
      }
    },
    formChanged() {
      this.errors.clear();
      this.$validator.validate('Email');
    },
    toggleSaveAuth(isChecked) {
      if (isChecked) {
        window.localStorage.setItem(SAVING_KEY, this.activeTab);
      } else {
        window.localStorage.removeItem(SAVING_KEY);
      }
    },
    setActiveTab(tab) {
      this.activeTab = tab;
      this.errors.clear();
    },
  },
};
</script>
<style lang="scss" scoped>
.modal-container {
  .close-box-btn {
    cursor: pointer;
    border-radius: 90%;
    border: 1px solid black;
    width: 48px;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #30374a;
    background-color: #181c25;
  }
}
.modal-container {
  .position-btn-close {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    padding-top: 20px;
  }
}
.modal-container {
  border-radius: 16px;
  background: #151520 !important;
  max-height: 650px;
  flex-direction: column;
  align-items: center;
  min-height: 40dvh;
  max-height: 90dvh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: 'Alexandria', sans-serif;
  max-width: 625px;
  min-width: 625px;
  @media (max-width: 915px) {
    max-width: 525px;
    min-width: 525px;
  }
  @media (max-width: 600px) {
    max-width: 455px;
    min-width: 455px;
  }
  @media (max-width: 450px) {
    max-width: 325px;
    min-width: 325px;
  }
  @media (max-height: 600px) {
    overflow-y: scroll;
    justify-content: flex-start;
  }
  > .wraper-content {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    min-width: 84%;
    gap: 26px;
    padding: 40px 32px;
    @media (max-width: 600px) {
      padding: 40px 20px;
    }

    > .wraper-title {
      display: flex;
      flex-direction: column;
      gap: 4px;

      > .header {
        margin: 0 !important;

        font-size: 32px;
        font-style: normal;
        font-weight: 600;
        line-height: 135%; /* 43.2px */
        letter-spacing: -0.48px;
        background: var(
          --Title,
          linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.5) 5.29%,
            rgba(255, 255, 255, 0.91) 51.55%,
            rgba(255, 255, 255, 0.5) 100%
          )
        );
        background-clip: content-box;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      > .text-modal {
        margin: 0 !important;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        color: rgba(255, 255, 255, 0.5);
        text-align: center;
        > a {
          color: var(--opacity-700, rgba(255, 255, 255, 0.8));
          cursor: pointer;
          &:hover {
            color: #5a58f2;
          }
        }
      }
    }
    > form.form-input {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      align-self: stretch;
      width: 100%;

      > .new-signup-btn {
        background-color: white !important;
        display: flex;
        padding: 16px;
        justify-content: center;
        align-items: center;
        gap: 12px;
        align-self: stretch;
        color: #121217 !important;
        text-align: center;
        /* Body/Body-16/Medium */
        font-family: 'Alexandria', sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
        margin-top: 10px;
      }
    }
  }
  .sign-in-loader {
    width: 100vw !important;
    > .modalbackdrop_container {
      background: none !important;
      background-color: transparent !important;
    }
  }
}
</style>
